import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { sysColors } from "@/assets/theme/colors";
import { ChartData } from "./Chart";
import ChartTooltip from "./ChartTooltip";

import classNames from "classnames";
import ChartSkeleton from "@/components/Skeleton/ChartSkeleton";
import { formatDisplayCurrency } from "@/lib/currencyHelpers";
import ChartLegend from "./ChartLegend";

const COLORS = [
  sysColors["sys-brand-primary"],
  sysColors["sys-brand-secondary"],
  sysColors["sys-brand-tertiary"],
  sysColors["sys-brand-primary-container"]
];

export type PieChartProps = {
  // Chart data
  data: ChartData;
  colors?: string[];

  // Content
  title: string;
  totalValue?: number;

  // State
  loading?: boolean;

  // Layout
  className?: string;
};

const PieChartComponent = (props: PieChartProps) => {
  const { data, title, colors = COLORS, totalValue, loading = false, className } = props;
  const chartData = data.map((item) => ({ ...item, label: item.value }));

  // Calculate total value if not provided
  const displayTotal = totalValue ?? data.reduce((acc, item) => acc + item.value, 0);

  return (
    <div
      className={classNames(
        "flex flex-col items-start w-full h-full",
        "border border-sys-brand-outline rounded-lg p-6",
        className
      )}
    >
      {/* Header */}
      <div className="w-full">
        <p className="text-title-medium text-sys-brand-on-surface">{title}</p>
        {displayTotal > 0 && (
          <div className="text-display-small text-sys-brand-on-surface">${formatDisplayCurrency(displayTotal)}</div>
        )}
      </div>

      {/* Chart */}
      <div className="w-full">
        {loading ? (
          <ChartSkeleton type="pie" />
        ) : (
          <>
            <ResponsiveContainer width="100%" height={225}>
              <PieChart>
                <Pie data={chartData} dataKey="value" cx="50%" cy="50%" outerRadius={90} fill="#8884d8">
                  {chartData.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active = false, payload }) => {
                    const items =
                      payload?.map((entry: any) => ({
                        name: entry.payload.name,
                        value: entry.value,
                        color: entry.payload.fill || entry.color
                      })) || [];

                    return <ChartTooltip active={active} title={title} items={items} formatAsCurrency={true} />;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>

            {/* Legend */}
            <ChartLegend
              items={chartData.map((entry, index) => ({
                name: entry.name,
                color: colors[index % colors.length],
                visible: true
              }))}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default PieChartComponent;
